import type { OwnProduct, OwnProductInput } from '../api/contracts'

/**
 * Черновик карточки товара — то, что человек набирает в форме, строками полей. В тело записи он превращается только
 * через `inputOf`: края обрезаются, пустое поле — `null`, штрихкоды — по одному в строке. Проверка здесь — подсказка до
 * отправки; окончательно решает служба, и её текст отказа показывается дословно.
 */
export interface ProductDraft {
  name: string
  fullName: string
  sku: string
  unitCode: string
  unitName: string
  /** `'true'` — весовой, `'false'` — штучный, `''` — не определён. */
  isWeighted: '' | 'true' | 'false'
  /** `''` — без группы. */
  groupId: string
  /** Код ставки чека; `''` — не указана. */
  vatRate: string
  /** Код тега 1212 строкой; `''` — не указан. */
  paymentSubject: string
  /** Код тега 1214 строкой; `''` — не указан. */
  paymentMethod: string
  isMarked: boolean
  /** Штрихкоды — по одному в строке. */
  barcodes: string
}

export type ProductDraftMode = 'create' | 'edit'

/** Новый товар: единица «шт», без группы; весовой признак и кассовые реквизиты человек выбирает сам — без них товар не продать. */
export const NEW_PRODUCT_DRAFT: ProductDraft = {
  name: '',
  fullName: '',
  sku: '',
  unitCode: '796',
  unitName: 'шт',
  isWeighted: '',
  groupId: '',
  vatRate: '',
  paymentSubject: '',
  paymentMethod: '',
  isMarked: false,
  barcodes: '',
}

/** Пределы полей — как в службе (`OwnProduct`). */
export const PRODUCT_LIMITS = { name: 512, fullName: 1024, sku: 128, unitCode: 8, unitName: 32, barcode: 128, barcodes: 100 } as const

export function draftOf(product: OwnProduct): ProductDraft {
  return {
    name: product.name,
    fullName: product.fullName ?? '',
    sku: product.sku ?? '',
    unitCode: product.unitCode ?? '',
    unitName: product.unitName ?? '',
    isWeighted: product.isWeighted === null ? '' : product.isWeighted ? 'true' : 'false',
    groupId: product.groupId ?? '',
    vatRate: product.vatRate ?? '',
    paymentSubject: product.paymentSubject === null ? '' : String(product.paymentSubject),
    paymentMethod: product.paymentMethod === null ? '' : String(product.paymentMethod),
    isMarked: product.isMarked,
    barcodes: product.barcodes.join('\n'),
  }
}

const optional = (value: string): string | null => (value.trim() === '' ? null : value.trim())

const codeOf = (value: string): number | null => (value.trim() === '' ? null : Number.parseInt(value, 10))

export function barcodesOf(text: string): readonly string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

export function inputOf(draft: ProductDraft): OwnProductInput {
  return {
    name: draft.name.trim(),
    fullName: optional(draft.fullName),
    sku: optional(draft.sku),
    unitCode: optional(draft.unitCode),
    unitName: optional(draft.unitName),
    isWeighted: draft.isWeighted === '' ? null : draft.isWeighted === 'true',
    groupId: optional(draft.groupId),
    vatRate: optional(draft.vatRate),
    paymentSubject: codeOf(draft.paymentSubject),
    paymentMethod: codeOf(draft.paymentMethod),
    isMarked: draft.isMarked,
    barcodes: barcodesOf(draft.barcodes),
  }
}

/**
 * Первая причина, по которой черновик не отправить, словами; `null` — можно отправлять. У нового товара ставка, весовой
 * признак, предмет и способ расчёта обязательны: товар без них в чек не попадёт (служба отказывает в продаже, умолчаний нет).
 */
export function draftProblem(draft: ProductDraft, mode: ProductDraftMode): string | null {
  const name = draft.name.trim()
  if (name === '') return 'Укажите наименование'
  if (name.length > PRODUCT_LIMITS.name) return `Наименование — не длиннее ${PRODUCT_LIMITS.name} символов`
  if (draft.fullName.trim().length > PRODUCT_LIMITS.fullName) return `Полное наименование — не длиннее ${PRODUCT_LIMITS.fullName} символов`
  if (draft.sku.trim().length > PRODUCT_LIMITS.sku) return `Артикул — не длиннее ${PRODUCT_LIMITS.sku} символов`
  const unitCode = draft.unitCode.trim()
  if (unitCode !== '' && !/^\d{1,8}$/.test(unitCode)) return 'Код единицы — цифры по ОКЕИ (796 — штука, 166 — килограмм)'
  if (draft.unitName.trim().length > PRODUCT_LIMITS.unitName) return `Единица — не длиннее ${PRODUCT_LIMITS.unitName} символов`
  const barcodes = barcodesOf(draft.barcodes)
  if (barcodes.length > PRODUCT_LIMITS.barcodes) return `У товара не больше ${PRODUCT_LIMITS.barcodes} штрихкодов`
  const seen = new Set<string>()
  for (const barcode of barcodes) {
    if (barcode.length > PRODUCT_LIMITS.barcode) return `Штрихкод ${barcode} длиннее ${PRODUCT_LIMITS.barcode} символов`
    if (seen.has(barcode)) return `Штрихкод ${barcode} указан дважды`
    seen.add(barcode)
  }
  if (mode === 'create') {
    if (draft.vatRate === '') return 'Выберите ставку НДС — без неё товар не продать'
    if (draft.isWeighted === '') return 'Выберите, штучный товар или весовой — без этого товар не продать'
    if (draft.paymentSubject === '') return 'Выберите предмет расчёта — без него товар не продать'
    if (draft.paymentMethod === '') return 'Выберите способ расчёта — без него товар не продать'
  }
  return null
}

/** Чего не хватает товару для продажи (для карточки уже записанного товара): пусто — продать можно. */
export function saleGaps(
  product: Pick<OwnProduct, 'vatRate' | 'unitCode' | 'unitName' | 'isWeighted' | 'paymentSubject' | 'paymentMethod'>,
): readonly string[] {
  const gaps: string[] = []
  if (product.vatRate === null) gaps.push('ставка НДС')
  if (product.unitCode === null || product.unitCode.trim() === '') gaps.push('единица измерения')
  if (product.unitName === null || product.unitName.trim() === '') gaps.push('имя единицы измерения')
  if (product.isWeighted === null) gaps.push('весовой признак')
  if (product.paymentSubject === null) gaps.push('предмет расчёта')
  if (product.paymentMethod === null) gaps.push('способ расчёта')
  return gaps
}
